/**
 * Helpers de formato temporal en español.
 *
 * Pensados para la UI (tarjetas de actividades recientes, fechas en el
 * dashboard…). Sin dependencias externas: solo `Date` nativo + `Intl`
 * con locale `es-ES`. Esto evita arrastrar `date-fns`/`dayjs` solo para
 * formato relativo y mantiene el bundle del frontend ligero.
 *
 * Decisiones:
 * - `formatRelativeTime` siempre devuelve algo (nunca lanza ni devuelve
 *   `null`). Si la fecha es inválida se devuelve el string original.
 * - El umbral "ayer" usa 48h en lugar de "día calendario distinto" porque
 *   en la UX de actividades importa más cuánto hace que pasó, no si
 *   técnicamente fue ayer a las 23:00 o esta madrugada a las 02:00.
 * - Para fechas > 7d usamos `formatDateShort` (ej. "15 jun"); si la fecha
 *   es de otro año añadimos el año explícitamente (ej. "15 jun 2024").
 * - Aceptamos `now` por parámetro para permitir tests deterministas.
 */

const ONE_MINUTE_MS = 60 * 1000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

/**
 * Devuelve un texto relativo en español: "ahora mismo", "hace 5 min",
 * "hace 3 h", "ayer", "hace 4 días" o fecha corta (`15 jun`) si han
 * pasado >= 7 días.
 *
 * Si la fecha está en el futuro (caso raro: drift de reloj), igualmente
 * devolvemos "ahora mismo" en vez de un negativo extraño.
 */
export function formatRelativeTime(isoDate: string, now: Date = new Date()): string {
  const then = new Date(isoDate).getTime();
  if (!Number.isFinite(then)) return isoDate;
  const diffMs = now.getTime() - then;
  if (diffMs < ONE_MINUTE_MS) return 'ahora mismo';

  if (diffMs < ONE_HOUR_MS) {
    const mins = Math.max(1, Math.floor(diffMs / ONE_MINUTE_MS));
    return `hace ${mins} min`;
  }

  if (diffMs < ONE_DAY_MS) {
    const hours = Math.max(1, Math.floor(diffMs / ONE_HOUR_MS));
    return `hace ${hours} h`;
  }

  if (diffMs < 2 * ONE_DAY_MS) {
    return 'ayer';
  }

  if (diffMs < 7 * ONE_DAY_MS) {
    const days = Math.floor(diffMs / ONE_DAY_MS);
    return `hace ${days} días`;
  }

  return formatDateShort(isoDate, now);
}

/**
 * Formato corto de fecha en español. Para el año actual: "15 jun".
 * Para años distintos al de `now`: "15 jun 2024".
 *
 * Usa timezone UTC porque las fechas que recibimos del backend son ISO en
 * UTC y queremos un comportamiento determinista (no dependiente de la TZ
 * del navegador del usuario).
 */
export function formatDateShort(isoDate: string, now: Date = new Date()): string {
  const d = new Date(isoDate);
  if (!Number.isFinite(d.getTime())) return isoDate;
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: sameYear ? undefined : 'numeric',
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return isoDate.slice(0, 10);
  }
}

/**
 * "junio 2026" — usado por el calendario mensual como encabezado.
 * Capitalizamos manualmente la primera letra porque `Intl` en `es-ES`
 * devuelve los meses en minúscula.
 */
export function formatMonthYear(year: number, monthIndex: number): string {
  try {
    const d = new Date(Date.UTC(year, monthIndex, 1));
    const raw = new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  } catch {
    return `${monthIndex + 1}/${year}`;
  }
}
